/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import * as z from 'zod';
/**
 * Commands the PA Agent sends to the user's live app (`app_command` transcript blocks). Mirrors
 * the engineer agent's `contract/app_commands.py`; keep both in step. The agent names resources
 * and views, never URLs: `resourcePath` and `appViewPath` below turn them into window paths.
 */
export const AGENT_APP_VIEWS = [
    'pvsim.results',
    'pvsim.config',
    'pvsim.new',
    'pvcosting.results',
    'pvcosting.config',
    'esscosting.results',
    'esscosting.config',
    'plantsim.results',
    'plantsim.config',
    'net_benefit.results',
    'net_benefit.new',
    'project_simulate',
    'land_scoring',
    'create_parcels',
    'database',
    'projects',
    'bid_requests',
];
const APP_VIEW_PATHS = {
    'pvsim.results': '/pv/pvsim/results',
    'pvsim.config': '/pv/pvsim/config',
    'pvsim.new': '/pv/pvsim/new',
    'pvcosting.results': '/pv/costing',
    'pvcosting.config': '/pv/costing/configurations',
    'esscosting.results': '/ess/costing',
    'esscosting.config': '/ess/costing/configurations',
    'plantsim.results': '/plant-v2',
    'plantsim.config': '/plant-v2/configurations',
    // Net benefit is part of PlantSim sweep results: both open the sweeps, where it runs per sweep.
    'net_benefit.results': '/plant-v2?type=sweep',
    'net_benefit.new': '/plant-v2?type=sweep',
    project_simulate: '/projects/simulate',
    land_scoring: '/projects/land-scoring',
    create_parcels: '/projects/create-parcels',
    database: '/db',
    projects: '/projects',
    bid_requests: '/bid-requests',
};
export const appViewPath = (view) => APP_VIEW_PATHS[view];
const configPath = (basePath, { resource_id, version }) => {
    const params = new URLSearchParams({ config: resource_id });
    if (version !== null && version !== undefined)
        params.set('version', String(version));
    return `${basePath}?${params}`;
};
const recordPath = (basePath, { resource_id }) => `${basePath}/${encodeURIComponent(resource_id)}`;
/** The app page of a resource, or `undefined` for resource types the app has no page for. */
export const resourcePath = (reference) => {
    switch (reference.resource_type) {
        case 'pvsim_config':
            return configPath('/pv/pvsim/new', reference);
        case 'pvcosting_config':
            return configPath('/pv/costing/simulate', reference);
        case 'esscosting_config':
            return configPath('/ess/costing/simulate', reference);
        case 'plantsim_config':
            return configPath('/plant-v2/simulate', reference);
        case 'pvsim_run':
            return `/pv/pvsim/result?${new URLSearchParams({ scenario: reference.resource_id })}`;
        case 'weather':
            return recordPath('/db/weather', reference);
        case 'met_station':
            return recordPath('/db/met-stations', reference);
        case 'pv_module':
            return recordPath('/db/pvmodules', reference);
        case 'pv_inverter':
            return recordPath('/db/pvinverters', reference);
        case 'battery':
            return recordPath('/db/batteries', reference);
        case 'ess_inverter':
            return recordPath('/db/essinverters', reference);
        case 'mv_transformer':
            return recordPath('/db/mv-transformers', reference);
        case 'project':
            return `/?project=${encodeURIComponent(reference.resource_id)}`;
        default:
            return undefined;
    }
};
const commandId = z.string().min(1);
/**
 * What an agent write to app state does: `navigation` changes what a window shows, `draft_edit`
 * edits unsaved values the user saves, `confirmation_request` asks the user to confirm an action.
 */
export const AGENT_CAPABILITY_EFFECTS = [
    'navigation',
    'draft_edit',
    'confirmation_request',
];
export const agentResourceReferenceSchema = z.object({
    resource_type: z.string(),
    resource_id: z.string().min(1),
    version: z.number().int().nullable(),
    label: z.string(),
});
export const agentAppCommandSchema = z.discriminatedUnion('kind', [
    z.object({
        command_id: commandId,
        kind: z.literal('open_resource'),
        resource: agentResourceReferenceSchema,
    }),
    z.object({
        command_id: commandId,
        kind: z.literal('open_view'),
        view: z.enum(AGENT_APP_VIEWS),
        label: z.string(),
    }),
    z.object({
        command_id: commandId,
        kind: z.literal('set_view_state'),
        window_id: z.string().min(1),
        key: z.string().min(1),
        /** Window path (including search) the value applies to; `null` for window-wide state. */
        view: z.string().nullable(),
        mode: z.enum(['replace', 'merge']),
        /** The agent's patch (merge) or value (replace), not the merged result. */
        value: z.json(),
        label: z.string(),
        // Defaults keep commands recorded before capability contracts readable.
        effect: z.enum(AGENT_CAPABILITY_EFFECTS).default('draft_edit'),
        /** The capability's `revision` the agent read; the app refuses the write when it changed. */
        expected_revision: z.string().nullable().default(null),
        /** The capability's `contract_version` the agent read. */
        contract_version: z.string().nullable().default(null),
    }),
]);
/**
 * Where an app command is: `sent` until the tab it was sent to reports what it did (an
 * `app_command_ack`). `confirmation_pending` waits for the user, who `confirmed` or `declined`.
 */
export const AGENT_APP_COMMAND_STATUSES = [
    'sent',
    'applied',
    'rejected',
    'confirmation_pending',
    'confirmed',
    'declined',
];
export const AGENT_STATE_LIMITS = {
    schemaBytes: 16 * 1024,
    valueBytes: 32 * 1024,
    totalBytes: 128 * 1024,
    windows: 20,
    statesPerWindow: 20,
};
//# sourceMappingURL=agent-app-commands.js.map